/**
 * `usePlanContext()` — lightweight, shared read of the customer's current plan
 * and whether it is a "custom" (on-request) plan that must route to sales
 * instead of self-serve checkout.
 *
 * The subscription catalog (`AuthRepository.getSubscriptionPlans`) is fetched
 * once and memoised at module scope so the header Upgrade CTA can be dropped
 * onto every subscription sub-page without each instance re-fetching. The
 * current plan is resolved from `useTokenBalanceStore.planKeys` (the JWT-backed
 * `plan-<key>` list) against the FULL catalog (including on-request plans, which
 * `useSubscriptionManagement` filters out of its self-serve list).
 */
import { computed, ref } from 'vue';
import AuthRepository from '@/repositories/AuthRepository';
import { useEntitlementsStore } from '@/stores/useEntitlementsStore';
import { useTokenBalanceStore } from '@/stores/useTokenBalanceStore';

/**
 * Billing states whose access is operator-granted (internal company,
 * sales-provisioned, or grandfathered) rather than a self-serve Stripe plan.
 * These accounts cannot self-serve a plan change — the upgrade page routes them
 * to a sales-contact form instead of Stripe checkout. Mirrors scrumble's
 * `BillingAccountState` enum (Internal / ManuallyGranted / Grandfathered).
 */
export const SALES_MANAGED_BILLING_STATES = Object.freeze([
  'internal',
  'manually_granted',
  'grandfathered',
]);

/**
 * Stripe price ids carried on a `/me` subscription row. scrumble serialises them
 * both on the row itself (`stripe_price`) and per line item (`items[].stripe_price`).
 *
 * ponytail: reads those two known shapes, not an arbitrary deep walk — widen it
 * if the `/me` payload ever nests subscription items differently.
 */
function subscriptionPriceIds(row) {
  if (!row || typeof row !== 'object') return [];
  const ids = [];
  if (typeof row.stripe_price === 'string') ids.push(row.stripe_price);
  if (Array.isArray(row.items)) {
    ids.push(...row.items.map((item) => item?.stripe_price).filter((id) => typeof id === 'string'));
  }
  return ids;
}

/**
 * Resolve which catalog plan AND billing interval the tenant is subscribed to
 * RIGHT NOW, by matching their live Stripe price ids against the catalog.
 *
 * `plan_keys` (see `currentPlan` below) identifies the plan but carries no
 * interval, so it cannot tell a monthly customer from a yearly one. Every
 * upgrade surface needs that distinction: presenting `month` to a yearly
 * customer makes their plan change silently rewrite the billing term too,
 * refunding the unused year as proration on an `always_invoice` swap.
 *
 * @param {Array<object>} plans Catalog plan rows (carry `stripe_price_month`/`_year`).
 * @param {Array<object>} subscriptions `/me` subscription rows.
 * @returns {{key: string, interval: 'month'|'year'}|null} null when nothing matches.
 */
export function findCurrentPlanMatch(plans, subscriptions) {
  for (const priceId of (subscriptions ?? []).flatMap(subscriptionPriceIds)) {
    for (const plan of plans ?? []) {
      if (plan?.stripe_price_month === priceId) return { key: plan.key, interval: 'month' };
      if (plan?.stripe_price_year === priceId) return { key: plan.key, interval: 'year' };
    }
  }
  return null;
}

let cachedCatalog = null;
let inflight = null;
const catalogRef = ref(null);

async function ensureCatalog() {
  if (cachedCatalog) {
    catalogRef.value = cachedCatalog;
    return cachedCatalog;
  }
  if (!inflight) {
    inflight = AuthRepository.getSubscriptionPlans()
      .then(({ data }) => {
        cachedCatalog = data || null;
        catalogRef.value = cachedCatalog;
        return cachedCatalog;
      })
      .catch(() => null)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Test-only: drop the memoised catalog so each spec starts clean. */
export function __resetPlanContextCache() {
  cachedCatalog = null;
  inflight = null;
  catalogRef.value = null;
}

export function usePlanContext() {
  const tokenStore = useTokenBalanceStore();
  const entitlementsStore = useEntitlementsStore();

  const plans = computed(() => [...(catalogRef.value?.plans ?? []), ...(catalogRef.value?.legacy_plans ?? [])]);
  const currentPlan = computed(() => {
    const keys = (tokenStore.planKeys || []).map((key) => String(key).replace(/^plan-/, ''));
    if (!keys.length) return null;
    return plans.value.find((plan) => keys.includes(plan.key)) || null;
  });
  // The account is sales-managed (internal / manually-granted / grandfathered):
  // its access is operator-granted, not a self-serve Stripe plan, so it can't
  // self-serve a plan change.
  const isSalesManagedAccount = computed(() =>
    SALES_MANAGED_BILLING_STATES.includes(entitlementsStore.billingAccountState)
  );
  // "Custom" = route to sales instead of Stripe checkout. True when the resolved
  // plan is sold on request, OR the account is operator-managed. NOTE: an
  // on-request company customer (and the internal Profiteia tenant) carries no
  // self-serve `plan_keys`, so the billing-state signal — not plan matching — is
  // what catches them.
  const isCustomPlan = computed(
    () => Boolean(currentPlan.value?.on_request) || isSalesManagedAccount.value
  );

  return {
    plans,
    currentPlan,
    isSalesManagedAccount,
    isCustomPlan,
    ensureCatalog,
  };
}
